import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Form,
  Row,
  Col,
  Select,
  InputNumber,
  Typography,
  Divider,
  Tag,
  Tooltip,
  Slider,
  Rate,
  Avatar,
  Space,
  Modal,
  Checkbox,
} from "antd";
import Input from "../../../../components/input";
import FormLayout, { FormContent, FormFooter, FormHeader } from "../../../../components/form/form";
import { TAssesment, TKPI } from "../../../../types/config";
import {
  eASSESSMENT_STATUS,
  eEVALUATION_CRITERIA,
  ePERFORMANCE_RATING,
  eResultCode,
  eRoleUniqueId,
} from "../../../../utils/enum";
import {
  AddEditAssessment,
  addEditAssessmentReview,
  GetKPIs,
  getSpecificAssessment,
  employeeAssessmentAction,
} from "../../../../utils/api.constant";
import useFetch from "../../../../hooks/useFetch";
import { PERFORMANCE_RATING_LABELS } from "../../../../utils/constants";
import {
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  UserOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  MobileOutlined,
} from "@ant-design/icons";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import utils from "../../../../utils";
import { UserContext } from "../../../../context/user";
import TextArea from "antd/es/input/TextArea";
import Utils from "../../../../utils";
import styles from "../assessment.module.css";

const { Text, Title } = Typography;

type TDrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

// const PercentageInput = ({
//   inputValue,
//   onChange,
// }: {
//   inputValue?: number;
//   onChange?: (value: number | null) => void;
// }) => {
//   const [value, setValue] = useState<number>(inputValue ?? 0);

//   const handleChange = (val: number | null) => {
//     const newVal = val ?? 0;
//     setValue(newVal);
//     onChange?.(newVal);
//   };
//   console.log("inputValue", inputValue);
//   return (
//     <Row gutter={12} align="middle">
//       <Col flex="auto">
//         <Slider
//           min={0}
//           max={100}
//           defaultValue={typeof inputValue === "number" ? inputValue : 0}
//           value={value}
//           onChange={handleChange}
//           marks={{ 0: "0%", 25: "25%", 50: "50%", 75: "75%", 100: "100%" }}
//         />
//       </Col>
//       <Col flex="80px">
//         <InputNumber
//           min={0}
//           max={100}
//           value={value}
//           onChange={handleChange}
//           style={{ width: "100%" }}
//           formatter={(v) => `${v}%`}
//           parser={(v) => Number(v?.replace("%", ""))}
//         />
//       </Col>
//     </Row>
//   );
// };

const PercentageInput = ({
  value = 0,
  onChange,
  disabled = false,
}: {
  value?: number;
  onChange?: (value: number | null) => void;
  disabled?: boolean;
}) => {
  const handleChange = (val: number | null) => {
    if (!disabled) {
      onChange?.(val ?? 0);
    }
  };

  return (
    <Row gutter={12} align="middle">
      <Col flex="auto">
        <Slider
          min={0}
          max={100}
          value={value}
          disabled={disabled}
          onChange={handleChange}
          marks={{ 0: "0%", 25: "25%", 50: "50%", 75: "75%", 100: "100%" }}
        />
      </Col>

      <Col flex="80px">
        <InputNumber
          min={0}
          max={100}
          value={value}
          disabled={disabled}
          onChange={handleChange}
          style={{ width: "100%" }}
          formatter={(v) => `${v}%`}
          parser={(v) => Number(v?.replace("%", ""))}
        />
      </Col>
    </Row>
  );
};

const TimeInput = ({
  inputValue,
  onChange,
  disabled,
}: {
  inputValue?: { h?: number; m?: number } | string;
  onChange?: (inputValue: string) => void;
  disabled?: boolean;
}) => {
  // Parse initial value if it's a string "HH:MM"
  let initialHours = 0;
  let initialMinutes = 0;

  if (typeof inputValue === "string" && inputValue.includes(":")) {
    const parts = inputValue.split(":");
    initialHours = parseInt(parts[0], 10);
    initialMinutes = parseInt(parts[1], 10);
  } else if (typeof inputValue === "object") {
    initialHours = inputValue?.h || 0;
    initialMinutes = inputValue?.m || 0;
  }

  const [hours, setHours] = useState<number | null>(initialHours || null);
  const [minutes, setMinutes] = useState<number | null>(initialMinutes || null);

  const handleTimeChange = (newHours: number | null, newMinutes: number | null) => {
    setHours(newHours);
    setMinutes(newMinutes);
    if (newHours !== null && newMinutes !== null) {
      if (onChange) {
        onChange(`${newHours.toString().padStart(2, "0")}:${newMinutes.toString().padStart(2, "0")}`);
      }
    } else {
      if (onChange) onChange("");
    }
  };

  return (
    <Row gutter={8}>
      <Col span={12}>
        <InputNumber
          min={0}
          placeholder="HH"
          value={hours}
          onChange={(val) => handleTimeChange(val, minutes)}
          style={{ width: "100%" }}
          addonAfter="h"
          disabled={disabled}
        />
      </Col>
      <Col span={12}>
        <InputNumber
          min={0}
          max={59}
          placeholder="MM"
          value={minutes}
          onChange={(val) => handleTimeChange(hours, val)}
          style={{ width: "100%" }}
          addonAfter="m"
          disabled={disabled}
        />
      </Col>
    </Row>
  );
};

const AmountInput = ({
  inputValue,
  onChange,
  disabled = false,
}: {
  inputValue?: number;
  onChange?: (inputValue: number | null) => void;
  disabled?: boolean;
}) => {
  return (
    <InputNumber
      style={{ width: "100%" }}
      formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
      parser={(value) => value?.replace(/\₹\s?|(,*)/g, "") as unknown as number}
      value={inputValue}
      onChange={onChange}
      placeholder="Enter Amount"
      disabled={disabled}
    />
  );
};

export default function ViewPerformanceForm(props: TDrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { onShowToast } = useToast();
  const { post } = useFetch();
  const [kpiList, setKpiList] = useState<TKPI[]>([]);
  const { user } = useContext(UserContext);
  const isEmployee = user?.roles?.roleUniqueId === "CUSTOM_EMPLOYEE" || user?.roles?.name === "EMPLOYEE";

  const [managerResponses, setManagerResponses] = useState<any>({});

  const [remarkVisible, setRemarkVisible] = useState<{ [key: number]: boolean }>({});

  const [isManagerSubmitted, setIsManagerSubmitted] = useState(false);

  const [remarkModal, setRemarkModal] = useState<{
    visible: boolean;
    kpiId: number | null;
  }>({
    visible: false,
    kpiId: null,
  });

  const [actionModal, setActionModal] = useState<{
    visible: boolean;
    type: "ACCEPTED" | "REJECTED" | null;
    remarks: string;
  }>({
    visible: false,
    type: null,
    remarks: "",
  });

  const [statusName, setStatusName] = useState<number>(0);

  const [userDetails, setUserDetails] = useState<any>(null);

  useEffect(() => {
    fetchKPIs();
  }, []);

  const fetchKPIs = async () => {
    setIsLoading(true);
    try {
      if (props.id) {
        const payload = {
          data: {
            assessmentId: props.id, // Static for now as requested
          },
        };
        const response = await post(getSpecificAssessment, payload);
        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          const details = response?.data?.details;

          setUserDetails(response?.data?.user);
          setStatusName(response?.data?.statusName); // 👈 important
          setKpiList(details);
          if (details.some((kpi: TKPI) => kpi.manager_value !== null)) {
            setIsManagerSubmitted(true);
          }
          // 👇 IMPORTANT PART
          const initialValues: any = {};

          details.forEach((kpi: TKPI) => {
            initialValues[`employee_${kpi.kpiId}`] = kpi.value;
            initialValues[`remark_${kpi.kpiId}`] = kpi.self_comment;

            const currentStatus = response?.data?.statusName;
            const isAtManagerStage = currentStatus === eASSESSMENT_STATUS.MANAGER_REVIEW;
            const isTargetUserHR = response?.data?.user?.roles?.roleUniqueId === "HR" || response?.data?.user?.roles?.name === "HR";

            if (kpi.category === "BEHAVIORAL") {
              // At MANAGER_REVIEW stage, HR Assessment fields should be empty
              // (Admin fills them only at HR_REVIEW stage) except when the target employee is HR
              initialValues[`manager_${kpi.kpiId}`] = (isAtManagerStage && !isTargetUserHR) ? null : kpi.hr_value;
              initialValues[`managerRemark${kpi.kpiId}`] = (isAtManagerStage && !isTargetUserHR) ? null : kpi.hr_comment;
            } else {
              initialValues[`manager_${kpi.kpiId}`] = kpi.manager_value;
              initialValues[`managerRemark${kpi.kpiId}`] = kpi.manager_comment;
            }
          });

          // Set visibility of remarks if they exist
          const shownRemarks: { [key: number]: boolean } = {};
          details.forEach((kpi: TKPI) => {
            if (kpi.category === "BEHAVIORAL") {
              if (kpi.hr_comment) {
                shownRemarks[kpi.kpiId ?? (kpi.id as number)] = true;
              }
            } else {
              if (kpi.manager_comment) {
                shownRemarks[kpi.kpiId ?? (kpi.id as number)] = true;
              }
            }
          });
          setShow(shownRemarks);
          form.setFieldsValue({
            kpiName: details?.kpiName,
            reviewRemarks: response?.data?.reviewRemarks || "",
            ...initialValues,
          });
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching KPIs:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Failed to fetch KPIs",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openRemarkModal = (kpiId?: number) => {
    if (!kpiId) return;

    setRemarkModal({
      visible: true,
      kpiId,
    });

    // Optional: If you want to prefill
    const existingRemark = form.getFieldValue(`managerRemark${kpiId}`);
    if (existingRemark) {
      form.setFieldValue(`managerRemark${kpiId}`, existingRemark);
    }
  };

  const onFinish = async (values: any) => {
    try {
      const kpiReviews = kpiList.map((kpi) => {
        const reviewValue = values[`manager_${kpi.kpiId}`];
        const remark = values[`managerRemark${kpi.kpiId}`];

        let formattedValue = reviewValue;
        // Time case handle
        if (kpi.criteria === eEVALUATION_CRITERIA.TIME) {
          if (typeof reviewValue === "string" && reviewValue.includes(":")) {
            const [h, m] = reviewValue.split(":");
            formattedValue = {
              h: Number(h),
              m: Number(m),
            };
          }

          // If already object, keep as is
          if (typeof reviewValue === "object" && reviewValue !== null && "h" in reviewValue && "m" in reviewValue) {
            formattedValue = reviewValue;
          }
        }

        return {
          kpiId: kpi.kpiId,
          value: kpi.value,
          self_comment: kpi.self_comment,
          manager_value: kpi.category === "TECHNICAL" ? formattedValue : kpi.manager_value,
          manager_comment: kpi.category === "TECHNICAL" ? remark : kpi.manager_comment,
          hr_value: kpi.category === "BEHAVIORAL" ? formattedValue : kpi.hr_value,
          hr_comment: kpi.category === "BEHAVIORAL" ? remark : kpi.hr_comment,
          isRemark: isShow[kpi.kpiId ?? (kpi.id as number)] ? true : false,
        };
      });

      const payload = {
        data: {
          assessmentId: props.id,
          reviewRemarks: values.reviewRemarks || "",
          kpiReviews,
        },
      };
      setIsLoading(true);

      const response = await post(addEditAssessmentReview, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  const handleActionOpen = (type: "ACCEPTED" | "REJECTED") => {
    setActionModal({
      visible: true,
      type,
      remarks: "",
    });
  };

  const handleActionSubmit = async () => {
    if (!actionModal.remarks.trim()) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Remarks are required.",
      });
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        data: {
          assessmentId: props.id,
          action: actionModal.type,
          remarks: actionModal.remarks,
        },
      };

      const response = await post(employeeAssessmentAction, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        setActionModal({ visible: false, type: null, remarks: "" });
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error submitting action:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "An error occurred while submitting action.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getCriteriaLabel = (criteria?: number) => {
    if (!criteria) return "";
    return utils.formatEnumLabel(eEVALUATION_CRITERIA[criteria]);
  };

  const renderInputByCriteria = (kpi: TKPI, isReadOnly = false, isManager: boolean, isHR: boolean) => {
    const { criteria, value, manager_value, hr_value } = kpi;
    let updatedValues: any = value;
    if (isManager) {
      updatedValues = manager_value;
    }
    if (isHR) {
      updatedValues = hr_value;
    }

    switch (criteria) {
      case eEVALUATION_CRITERIA.PERCENTAGE:
        return (
          <PercentageInput
            value={updatedValues != undefined && updatedValues != null ? Number(updatedValues) : undefined}
            disabled={isReadOnly}
          />
        );
      case eEVALUATION_CRITERIA.RATING:
        return (
          <Rate
            allowHalf
            disabled={isReadOnly}
            value={updatedValues != undefined && updatedValues != null ? Number(updatedValues) : undefined}
          />
        );
      case eEVALUATION_CRITERIA.TIME:
        return <TimeInput disabled={isReadOnly} inputValue={updatedValues} />;

      case eEVALUATION_CRITERIA.SELECTION:
        const options = Object.keys(PERFORMANCE_RATING_LABELS).map((key) => ({
          label: PERFORMANCE_RATING_LABELS[Number(key) as ePERFORMANCE_RATING],
          value: key,
        }));

        return (
          <Select
            key={kpi.kpiId}
            options={options}
            disabled={isReadOnly}
            value={updatedValues !== undefined && updatedValues !== null ? Number(updatedValues) : undefined}
            placeholder="Select Rating"
            allowClear
            menuItemSelectedIcon={<CheckOutlined />}
            dropdownStyle={{ minWidth: 250 }}
          />
        );
      case eEVALUATION_CRITERIA.NUMBER:
        return (
          <InputNumber
            min={0}
            placeholder="Enter value"
            style={{ width: "100%" }}
            disabled={isReadOnly}
            value={updatedValues !== undefined && updatedValues !== null ? Number(updatedValues) : undefined}
          />
        );
      case eEVALUATION_CRITERIA.AMOUNT:
        return (
          <AmountInput
            disabled={isReadOnly}
            inputValue={updatedValues !== undefined && updatedValues !== null ? Number(updatedValues) : undefined}
          />
        );
      default:
        return (
          <Input
            placeholder="Enter value"
            disabled={isReadOnly}
            value={updatedValues !== undefined && updatedValues !== null ? Number(updatedValues) : undefined}
          />
        );
    }
  };

  const renderUserDetails = () => (
    <div
      style={{
        height: "100%",
        padding: "24px",
        backgroundColor: "#f9fafb",
        borderRight: "1px solid #f0f0f0",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <Avatar
          size={80}
          icon={<UserOutlined />}
          style={{
            backgroundColor: "var(--bs-primary)",
            marginBottom: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {userDetails?.fullName
            ?.split(" ")
            .map((n: string) => n[0])
            .join("")}
        </Avatar>
        <Title level={4} style={{ marginBottom: "4px", color: "#1f2937" }}>
          {userDetails?.fullName || "Guest User"}
        </Title>
        <Tag color="var(--bs-primary)" bordered={false}>
          {userDetails?.designationDetails?.name || "No Designation"}
        </Tag>
      </div>

      <Divider style={{ margin: "12px 0" }} />

      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              padding: "8px",
              backgroundColor: "#e6f7ff",
              borderRadius: "50%",
              color: "var(--bs-primary-dark)",
            }}
          >
            <MailOutlined />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Email Address
            </Text>
            <Text strong style={{ fontSize: "13px" }}>
              {userDetails?.emailId || "N/A"}
            </Text>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              padding: "8px",
              backgroundColor: "#e6f7ff",
              borderRadius: "50%",
              color: "var(--bs-primary-dark)",
            }}
          >
            <MobileOutlined />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Mobile Number
            </Text>
            <Text strong style={{ fontSize: "13px" }}>
              {userDetails?.mobileNumber || "N/A"}
            </Text>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              padding: "8px",
              backgroundColor: "#f6ffed",
              borderRadius: "50%",
              color: "var(--bs-primary-dark)",
            }}
          >
            <BankOutlined />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Organization
            </Text>
            <Text strong style={{ fontSize: "13px" }}>
              {userDetails?.org?.name || "N/A"}
            </Text>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              padding: "8px",
              backgroundColor: "#fff7e6",
              borderRadius: "50%",
              color: "var(--bs-primary-dark)",
            }}
          >
            <SafetyCertificateOutlined />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Role
            </Text>
            <Text strong style={{ fontSize: "13px" }}>
              {userDetails?.roles?.name || "N/A"}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
  const [isShow, setShow] = useState<{ [key: number]: boolean }>({});

  return (
    <div style={{ display: "flex", height: "100%", width: "100%" }}>
      <div style={{ width: "20%", minWidth: "250px", height: "100%" }}>{renderUserDetails()}</div>
      <div style={{ width: "80%", height: "100%" }}>
        <FormLayout>
          <Form form={form} name="performance_form" onFinish={onFinish} layout="vertical" requiredMark="optional">
            <FormHeader>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CloseOutlined style={{ cursor: "pointer", color: "white" }} onClick={props.onCloseDrawer} />
                <span style={{ color: "white", fontSize: 16, fontWeight: 500 }}>Assessment</span>
              </div>
            </FormHeader>
            <FormContent>
              <Row gutter={[16, 16]}>
                {kpiList.map((kpi, index) => {
                  const isManagerStage =
                    statusName === eASSESSMENT_STATUS.MANAGER_REVIEW || statusName === eASSESSMENT_STATUS.DISCUSSION;
                  const isHRStage =
                    statusName === eASSESSMENT_STATUS.HR_REVIEW || statusName === eASSESSMENT_STATUS.DISCUSSION;
                  const isEmployeeVerify = statusName === eASSESSMENT_STATUS.EMPLOYEE_VERIFY;
                  const kpiId = kpi.kpiId ?? (kpi.id as number);
                  const privilegeList = user?.privilegeList || [];
                  const isSelfAssessment = user?.id === userDetails?.id;
                  const isManager = Utils.isUserHasAccess(privilegeList, "MANAGERKPIREVIEW");
                  const isHR = Utils.isUserHasAccess(privilegeList, "HRREVIEWKPI");
                  const isAdmin = Utils.isUserHasAccess(privilegeList, "ADMINKPIREVIEW");

                  const isManagerKPI = isManagerStage && kpi.category === "TECHNICAL";
                  const isHRKPI = isHRStage && kpi.category === "BEHAVIORAL";

                  let canReview = false;

                  const isTargetUserHR = userDetails?.roles?.roleUniqueId === "HR" || userDetails?.roles?.name === "HR";

                  if (isAdmin) {
                    // 👑 Admin can edit everything
                    // If target user is HR, Admin can edit both categories at MANAGER_REVIEW stage
                    if (isTargetUserHR) {
                      canReview = isManagerStage || isHRStage;
                    } else {
                      canReview = isManagerKPI || isHRKPI;
                    }
                  } else if (isManager) {
                    // 👨‍💼 Manager → only
                    canReview = !isSelfAssessment && isManagerKPI;
                  } else if (isHR) {
                    // 👩‍💼 HR → only others' KPI (not own)

                    canReview = !isSelfAssessment && isHRKPI;
                  } else {
                    canReview = false;
                  }


                  return (
                    <Col span={24} key={kpiId}>
                      <div
                        style={{
                          height: "100%",
                          padding: "16px",
                          backgroundColor: "#f9f9f9",
                          borderRadius: "8px",
                          border: "1px solid #f0f0f0",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* KPI Header */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 8,
                          }}
                        >
                          <Text strong>
                            {index + 1}. {kpi.kpiName}
                          </Text>

                          {kpi.criteria && <Tag color="black">{getCriteriaLabel(kpi.criteria)}</Tag>}
                        </div>

                        {/* Description */}
                        <div
                          style={{
                            marginBottom: 16,
                            color: "#666",
                            fontSize: 12,
                          }}
                        >
                          {kpi.description}
                        </div>

                        {/* Two Columns */}
                        <Row gutter={32}>
                          {/* Employee Section */}
                          <Col span={isEmployee && statusName === eASSESSMENT_STATUS.MANAGER_REVIEW ? 24 : 12}>
                            <Text strong style={{ display: "block", marginBottom: 8 }}>
                              Employee Response
                            </Text>

                            <Form.Item name={`employee_${kpiId}`} style={{ marginBottom: 0 }}>
                              {renderInputByCriteria(kpi, true, isManager, isHR)}
                            </Form.Item>

                            {kpi.isRemark && (
                              <Row style={{ marginTop: 12 }}>
                                <Col span={24}>
                                  <Text
                                    type="secondary"
                                    style={{
                                      fontSize: 12,
                                      display: "block",
                                      marginBottom: 4,
                                    }}
                                  >
                                    Employee Remark
                                  </Text>

                                  <Form.Item name={`remark_${kpiId}`} style={{ marginBottom: 0 }}>
                                    <TextArea
                                      rows={2}
                                      className={styles.assessmenttextarea}
                                      placeholder="No remark provided"
                                      disabled
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            )}
                          </Col>

                          {/* Manager Section */}
                          {!(isEmployee && statusName === eASSESSMENT_STATUS.MANAGER_REVIEW) && (
                            <Col span={12}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 8,
                                }}
                              >
                                <Text strong>
                                  {kpi.category === "BEHAVIORAL" ? "HR Assessment" : "Manager Assessment"}
                                </Text>

                                {/* <Checkbox
                                  disabled={!canReview}
                                  checked={isShow[kpiId]}
                                  onChange={(e) =>
                                    setShow((prev) => ({
                                      ...prev,
                                      [kpiId]: e.target.checked,
                                    }))
                                  }
                                >
                                  Add Remark
                                </Checkbox> */}
                              </div>

                              <Form.Item
                                name={`manager_${kpiId}`}
                                rules={[
                                  {
                                    required: canReview,
                                    message: "Rating required",
                                  },
                                ]}
                                style={{ marginBottom: 0 }}
                              >
                                {React.cloneElement(renderInputByCriteria(kpi, !canReview, isManager, isHR), {
                                  onChange: (val: any) => {
                                    if (!isManagerSubmitted) {
                                      setManagerResponses((prev: any) => ({
                                        ...prev,
                                        [kpiId]: val,
                                      }));
                                    }
                                  },
                                })}
                              </Form.Item>

                              <Row style={{ marginTop: 12 }}>
                                <Col span={24}>
                                  <Text
                                    type="secondary"
                                    style={{
                                      fontSize: 12,
                                      display: "block",
                                      marginBottom: 2,
                                    }}
                                  >
                                    {kpi.category === "BEHAVIORAL" ? "HR Remark" : "Manager Remark"}
                                  </Text>

                                  <Form.Item
                                    name={`managerRemark${kpiId}`}
                                    rules={[{ required: canReview, message: "This field is required" }]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Input.TextArea
                                      rows={2}
                                      placeholder="Enter your remark..."
                                      className={styles.assessmenttextarea}
                                      disabled={!canReview}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </Col>
                          )}
                        </Row>
                      </div>
                    </Col>
                  );
                })}
              </Row>

              {statusName === eASSESSMENT_STATUS.DISCUSSION && (
                <div
                  style={{
                    marginTop: 24,
                    padding: "16px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "8px",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    Final Review Remarks
                  </Text>
                  <Form.Item
                    name="reviewRemarks"
                    rules={[{ required: true, message: "Please provide final remarks for the discussion" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input.TextArea rows={4} placeholder="Enter final assessment discussion remarks..." />
                  </Form.Item>
                </div>
              )}
              {statusName === eASSESSMENT_STATUS.FINAL_ACCEPTANCE && (
                <div
                  style={{
                    marginTop: 24,
                    padding: "16px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "8px",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    Final Review Remarks
                  </Text>
                  <Form.Item name="reviewRemarks" style={{ marginBottom: 0 }}>
                    <Input.TextArea disabled rows={4} placeholder="No final remarks provided" />
                  </Form.Item>
                </div>
              )}
            </FormContent>

            <FormFooter className="formFooter">
              <Button
                onClick={props.onCloseDrawer}
                disabled={isLoading}
                // className="btnDimensions"
              >
                Close
              </Button>

              {!isEmployee &&
                (statusName === eASSESSMENT_STATUS.MANAGER_REVIEW ||
                  statusName === eASSESSMENT_STATUS.HR_REVIEW ||
                  statusName === eASSESSMENT_STATUS.DISCUSSION) && (
                  <Button type="primary" onClick={form.submit} loading={isLoading}>
                    Submit Assessment
                  </Button>
                )}

              {statusName === eASSESSMENT_STATUS.EMPLOYEE_VERIFY && user?.id === userDetails?.id && (
                <>
                  <Button type="primary" onClick={() => handleActionOpen("ACCEPTED")} loading={isLoading}>
                    Accept
                  </Button>
                  <Button type="primary" danger onClick={() => handleActionOpen("REJECTED")} loading={isLoading}>
                    Reject
                  </Button>
                </>
              )}

              {statusName === eASSESSMENT_STATUS.FINAL_ACCEPTANCE && user?.id === userDetails?.id && (
                <Button type="primary" onClick={() => handleActionOpen("ACCEPTED")} loading={isLoading}>
                  Accept Assessment
                </Button>
              )}
            </FormFooter>
          </Form>
        </FormLayout>
      </div>

      <Modal
        title={actionModal.type === "ACCEPTED" ? "Corrective Plan" : "1:1 Meeting"}
        open={actionModal.visible}
        onOk={handleActionSubmit}
        onCancel={() => setActionModal({ visible: false, type: null, remarks: "" })}
        confirmLoading={isLoading}
        okText="Submit"
        okButtonProps={{ danger: actionModal.type === "REJECTED" }}
      >
        <div style={{ marginBottom: "16px" }}>
          <Text strong>
            {actionModal.type === "ACCEPTED" ? "Please mention corrective plan" : "Please mention reason for rejection"}
          </Text>
        </div>
        <TextArea
          rows={4}
          placeholder="Enter remarks here..."
          value={actionModal.remarks}
          onChange={(e) => setActionModal((prev) => ({ ...prev, remarks: e.target.value }))}
        />
      </Modal>
    </div>
  );
}
